/* ═══════════════════════════════════════════════════════════════
   OBSCURA · SEZIONE "PERCHÉ OBSCURA" — VARIANTE RAIL (DARK)
   ───────────────────────────────────────────────────────────────
   Scroll orizzontale (pin + traslazione): PARTE 1 = il rischio
   (€35M, rossi Filmstrip) → spazio → PARTE 2 = "Perché scegliere
   OBSCURA" + 4 colonne-beneficio con rail ciano e numero fantasma.
   Sfondo navy a gradiente + aurora + griglia (linguaggio OBSCURA).

   FILE AUTOSUFFICIENTE — incolla in Cursor e usa <WhyRail/>.
   Richiede solo React 18. Gli stili/keyframe sono iniettati qui sotto.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import Btn from "./Btn";
import "./Btn.css";
import { F, type } from "../theme/tokens";

/* ───────── token + keyframe (iniettati una sola volta) ───────── */
const OBSCURA_CSS = `
:root{
  --accent:#009CDD; --accent-2:#59C9F4;
  --ease:cubic-bezier(.22,1,.36,1);
}
@keyframes obHzNudge{0%,100%{transform:translateX(0)}50%{transform:translateX(6px)}}
@keyframes obVtNudge{0%,100%{transform:translateY(0)}50%{transform:translateY(6px)}}
@keyframes ob-aurora-1{0%{transform:translate3d(-8%,-4%,0) scale(1)}50%{transform:translate3d(6%,4%,0) scale(1.1)}100%{transform:translate3d(-8%,-4%,0) scale(1)}}
@keyframes ob-aurora-2{0%{transform:translate3d(6%,0,0) scale(1.05)}50%{transform:translate3d(-6%,6%,0) scale(1)}100%{transform:translate3d(6%,0,0) scale(1.05)}}
@keyframes ob-aurora-3{0%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(-4%,-6%,0) scale(1.08)}100%{transform:translate3d(0,0,0) scale(1)}}
@keyframes ob-grid-drift{from{background-position:0 0}to{background-position:80px 80px}}
`;
function ObscuraStyle() {
  return <style dangerouslySetInnerHTML={{ __html: OBSCURA_CSS }} />;
}

/* ───────── dati benefici ───────── */
const HZ_BENEFITS = [
  {
    n: "1",
    title: "Pensato per l'italiano",
    desc: "OBSCURA è ottimizzato per nomi, indirizzi e formati documentali italiani, con anonimizzazione dei volti e massima accuratezza su testi e immagini.",
  },
  {
    n: "2",
    title: "Zero data retention",
    desc: "Elaborazione in tempo reale su server EU e cancellazione immediata. Nessun file resta sui nostri server, garantendo la privacy by design.",
  },
  {
    n: "3",
    title: "Web App oppure API",
    desc: "Anonimizza dalla web app in autonomia oppure integra l'anonimizzazione nei tuoi processi via REST API: scegli il canale più adatto al tuo flusso di lavoro.",
  },
  {
    n: "4",
    title: "Il file resta utilizzabile",
    desc: "I dati sensibili spariscono, il senso del testo no. L'output anonimizzato rimane pronto per analisi dati, RAG, condivisione interna e workflow AI.",
  },
];
const HZ_SANCTIONS = [
  {
    id: "ai-act",
    label: "AI Act",
    amount: 35,
    amountUnit: "Milioni",
    turnover: "7%",
    sourceLabel: "Art. 99 Reg. (UE) 2024/1689",
    sourceUrl: "https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=OJ:L_202401689",
  },
  {
    id: "gdpr",
    label: "GDPR",
    amount: 20,
    amountUnit: "Milioni",
    turnover: "4%",
    sourceLabel: "Art. 83, co. 5 Reg. (UE) 2016/679",
    sourceUrl: "https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32016R0679",
  },
  {
    id: "nis2",
    label: "NIS2",
    amount: 10,
    amountUnit: "Milioni",
    turnover: "2%",
    sourceLabel: "Art. 34 Direttiva (UE) 2022/2555",
    sourceUrl: "https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32022L2555",
  },
];

const HZ_DARK_TRACKBG = "linear-gradient(90deg, rgba(251,191,36,.07) 0%, rgba(251,191,36,.02) 26%, rgba(0,156,221,.04) 62%, rgba(0,156,221,.09) 100%)";

/* ───────── sfondi decorativi ───────── */
function AuroraDark({ intensity = 1 }) {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", opacity: intensity }}>
      <div style={{ position: "absolute", width: "70vw", height: "70vw", left: "-20vw", top: "-25vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(0,156,221,.42), transparent 65%)", filter: "blur(40px)", animation: "ob-aurora-1 24s ease-in-out infinite" }} />
      <div style={{ position: "absolute", width: "60vw", height: "60vw", right: "-20vw", top: "20vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(89,201,244,.30), transparent 60%)", filter: "blur(50px)", animation: "ob-aurora-2 30s ease-in-out infinite" }} />
      <div style={{ position: "absolute", width: "50vw", height: "50vw", left: "40vw", bottom: "-15vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(77,186,231,.20), transparent 60%)", filter: "blur(60px)", animation: "ob-aurora-3 28s ease-in-out infinite" }} />
    </div>
  );
}

function GridOverlay({ color = "rgba(255,255,255,.07)", size = 64, opacity = 1, drift = true }) {
  return (
    <div aria-hidden style={{
      position: "absolute", inset: 0, pointerEvents: "none",
      backgroundImage: `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`,
      backgroundSize: `${size}px ${size}px`,
      opacity,
      animation: drift ? "ob-grid-drift 30s linear infinite" : "none",
      WebkitMaskImage: "radial-gradient(ellipse at center, #000 50%, transparent 100%)",
      maskImage: "radial-gradient(ellipse at center, #000 50%, transparent 100%)",
    }} />
  );
}

/* ───────── hook: pin + traslazione orizzontale ───────── */
function useHzScroll() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [x, setX] = useState(0);
  const [maxX, setMaxX] = useState(0);
  const [secH, setSecH] = useState("300vh");
  const [p, setP] = useState(0);

  useLayoutEffect(() => {
    const recalc = () => {
      const track = trackRef.current;
      if (!track) return;
      const vw = window.innerWidth;
      const max = Math.max(track.scrollWidth - vw, 0);
      setMaxX(max);
      setSecH((window.innerHeight + max) + "px");
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", recalc);
    const id = setTimeout(recalc, 250);
    const id2 = setTimeout(recalc, 800);
    return () => { ro.disconnect(); window.removeEventListener("resize", recalc); clearTimeout(id); clearTimeout(id2); };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const sec = sectionRef.current;
      if (!sec) return;
      const total = sec.offsetHeight - window.innerHeight;
      const top = -sec.getBoundingClientRect().top;
      const prog = total > 0 ? Math.min(Math.max(top / total, 0), 1) : 0;
      setP(prog);
      setX(prog * maxX);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [maxX]);

  return { sectionRef, trackRef, x, p, secH };
}

/* ───────── stage (sezione pinnata + track) ───────── */
function HzStage({ sectionRef, trackRef, x, secH, stickyBg, trackBg, grid, overlay, children }) {
  return (
    <section id="perche" ref={sectionRef} style={{ position: "relative", height: secH }}>
      <div style={{ position: "sticky", top: 0, height: "100vh", overflow: "hidden", background: stickyBg }}>
        {overlay}
        {grid && (
          <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, backgroundImage: grid, backgroundSize: "26px 26px" }}/>
        )}
        <div ref={trackRef} style={{
          position: "relative", zIndex: 1,
          display: "flex", alignItems: "center",
          height: "100%", width: "max-content",
          transform: `translate3d(${-x}px, 0, 0)`,
          willChange: "transform",
          background: trackBg,
        }}>
          {children}
        </div>
      </div>
    </section>
  );
}

const WHY_RAIL_CTA_STYLE = { borderRadius: 14 };
const WHY_RAIL_BG = "radial-gradient(120% 80% at 80% 0%, #0c2244 0%, #061427 60%, #050f1f 100%)";
const WHY_OBSCURA_INTRO = "L'anonimizzazione dei dati sensibili abbatte il rischio di sanzioni e rende la tua azienda compliant con GDPR, AI Act e NIS2.";

function HzScrollHint({ isMobile = false }) {
  const arrowBtn = (
    <span aria-hidden style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 40, height: 40, borderRadius: "50%",
      background: "var(--accent)", color: "#fff",
      boxShadow: "0 8px 22px rgba(0,156,221,.4)",
      animation: `${isMobile ? "obVtNudge" : "obHzNudge"} 1.6s var(--ease) infinite`,
    }}>
      {isMobile
        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12l7 7 7-7"/></svg>
        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>}
    </span>
  );

  return (
    <div style={{
      display: isMobile ? "flex" : "inline-flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: "center",
      width: isMobile ? "100%" : undefined,
      gap: isMobile ? 10 : 12,
    }}>
      <span style={{ ...type.label, fontSize: 11, letterSpacing: ".18em", color: "rgba(255,255,255,.5)", textAlign: isMobile ? "center" : undefined }}>COME PROTEGGERSI · SCORRI</span>
      {arrowBtn}
    </div>
  );
}

function WhyObscuraIntro({ onNavigate, isMobile = false }) {
  return (
    <div style={{ maxWidth: isMobile ? "none" : 400, paddingRight: isMobile ? 0 : 28 }}>
      <h2 style={{
        fontFamily: F.h,
        fontSize: isMobile ? 36 : "clamp(48px, 5vw, 72px)",
        fontWeight: 500,
        lineHeight: isMobile ? 1.02 : 0.98,
        letterSpacing: "-.035em",
        color: "#fff",
        margin: "0 0 20px",
      }}>
        Perché utilizzare <em style={{ fontStyle: "italic", color: "var(--accent-2)", fontWeight: 500 }}>OBSCURA</em>.
      </h2>
      <p style={{ fontFamily: F.b, fontSize: isMobile ? 16 : 17, lineHeight: 1.6, color: "rgba(255,255,255,.7)", marginBottom: 28 }}>
        {WHY_OBSCURA_INTRO}
      </p>
      <Btn
        variant="primaryLight"
        size="lg"
        onClick={onNavigate}
        style={{ ...WHY_RAIL_CTA_STYLE, ...(isMobile ? { width: "100%", justifyContent: "center" } : {}) }}
      >
        Anonimizza un documento
      </Btn>
    </div>
  );
}

/* ───────── card sanzioni con tab normative ───────── */
function SanctionCard({ isTablet }) {
  const [active, setActive] = useState(0);
  const s = HZ_SANCTIONS[active];
  const accent = "#f87171";
  const accentSoft = "#fca5a5";
  const accentBorder = "rgba(248,113,113,.28)";
  const accentGlow = "rgba(248,113,113,.16)";
  const tabId = (i) => `sanction-tab-${HZ_SANCTIONS[i].id}`;
  const panelId = "sanction-panel";

  return (
    <div style={{
      position: "relative", overflow: "hidden",
      background: "linear-gradient(155deg, rgba(255,255,255,.06) 0%, rgba(255,255,255,.02) 100%)",
      border: "1px solid rgba(89,201,244,.22)", borderRadius: 24,
      padding: isTablet ? 26 : 34, backdropFilter: "blur(8px)",
      boxShadow: "0 24px 50px rgba(6,20,39,.4)",
    }}>
      <div aria-hidden style={{ position: "absolute", right: -30, top: -30, width: 160, height: 160, borderRadius: "50%", background: `radial-gradient(circle, ${accentGlow}, transparent 70%)` }} />
      <div style={{ position: "relative" }}>
        <div
          id={panelId}
          role="tabpanel"
          aria-labelledby={tabId(active)}
          style={{ transition: "opacity .25s var(--ease)" }}
        >
          <span style={{ ...type.label, fontSize: 10.5, letterSpacing: ".2em", color: accent }}>SANZIONE MASSIMA</span>
          <div style={{ fontFamily: F.display, fontSize: isTablet ? 48 : 56, fontWeight: 800, lineHeight: .92, color: "#fff", margin: "10px 0 6px" }}>
            €{s.amount}<span style={{ fontSize: ".42em", color: accent }}>{s.amountUnit}</span>
          </div>
          <p style={{ fontFamily: F.b, fontSize: 13, color: "rgba(255,255,255,.6)", lineHeight: 1.5, marginBottom: 22 }}>
            oppure <strong style={{ color: "#fff" }}>{s.turnover}</strong> del fatturato totale annuo, se superiore.
            <br />
            Fonte:{" "}
            <a
              href={s.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "rgba(255,255,255,.78)", textDecoration: "none", fontWeight: 700 }}
            >
              {s.sourceLabel}
            </a>
          </p>
        </div>
        <div role="tablist" aria-label="Normative e sanzioni massime" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {HZ_SANCTIONS.map((reg, i) => {
            const selected = active === i;
            return (
              <button
                key={reg.id}
                type="button"
                role="tab"
                id={tabId(i)}
                aria-selected={selected}
                aria-controls={panelId}
                onClick={() => setActive(i)}
                style={{
                  ...type.label,
                  fontSize: 11,
                  letterSpacing: ".08em",
                  padding: "7px 13px",
                  borderRadius: 999,
                  cursor: "pointer",
                  border: `1px solid ${selected ? accent : accentBorder}`,
                  background: selected ? "rgba(248,113,113,.18)" : "rgba(255,255,255,.05)",
                  color: selected ? "#fff" : accentSoft,
                  transition: "all .25s var(--ease)",
                }}
              >
                {reg.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ───────── PARTE 1 — il rischio (rossi Filmstrip) ───────── */
function HzDarkRisk({ isTablet }) {
  const accent = "#f87171";
  return (
    <div style={{ flex: "0 0 auto", width: "100vw", height: "100%", display: "flex", alignItems: "center", padding: "0 clamp(28px, 6vw, 120px)" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: isTablet ? "1fr" : "minmax(0, 1.1fr) minmax(0, .9fr)",
        gap: isTablet ? 32 : 56, alignItems: "center", width: "100%", maxWidth: 1080,
      }}>
        <div>
          <h2 style={{ ...type.h2, color: "#fff", margin: "0 0 18px" }}>
            Cosa rischi se usi dati sensibili <em style={{ fontStyle: "italic", color: accent, fontWeight: 500 }}>senza anonimizzarli?</em>
          </h2>
          <p style={{ fontFamily: F.b, fontSize: isTablet ? 16 : 18, lineHeight: 1.6, color: "rgba(255,255,255,.72)", maxWidth: 520, marginBottom: 26 }}>
            GDPR, AI Act e NIS2 prevedono sanzioni fino a <strong style={{ color: "#fff" }}>€35M</strong>. L'anonimizzazione riduce l'esposizione prima che i documenti entrino nei tuoi workflow AI.
          </p>
          <HzScrollHint />
        </div>
        <SanctionCard isTablet={isTablet} />
      </div>
    </div>
  );
}

/* ───────── PARTE 2 — colonna beneficio (rail + numero fantasma) ───────── */
function RailColumn({ b, isMobile = false, isFirst = false }) {
  const [h, setH] = useState(false);
  const colPad = isMobile ? 24 : 38;
  return (
    <div
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        flex: "0 0 auto", position: "relative",
        scrollSnapAlign: isMobile ? "start" : undefined,
        width: isMobile ? 300 : 360,
        height: isMobile ? 420 : "min(62vh, 540px)",
        display: "flex", flexDirection: "column", justifyContent: "flex-start",
        padding: isMobile
          ? `clamp(80px, 16vh, 120px) 24px 30px ${colPad}px`
          : `clamp(120px, 22vh, 180px) 0 30px ${colPad}px`,
        marginLeft: isMobile ? (isFirst ? 0 : 20) : 40,
        borderLeft: "1px dashed rgba(255,255,255,.12)",
      }}
    >
      {/* rail ciano */}
      <span aria-hidden style={{
        position: "absolute", left: -1, top: "18%", width: 2, height: "64%",
        background: "linear-gradient(180deg, var(--accent-2), transparent)",
        opacity: h ? 1 : .4, transition: "opacity .35s var(--ease)",
      }}/>
      {/* numero fantasma — allineato al padding del testo (38px) */}
      <span aria-hidden style={{
        position: "absolute", left: colPad, top: 10,
        fontFamily: F.h, fontSize: isMobile ? 120 : 150, fontWeight: 800, lineHeight: 1,
        letterSpacing: "-.05em",
        color: h ? "rgba(89,201,244,.32)" : "rgba(89,201,244,.07)",
        textShadow: h ? "0 0 40px rgba(89,201,244,.18)" : "none",
        transition: "color .35s var(--ease), text-shadow .35s var(--ease)",
      }}>{b.n}</span>

      <div style={{ position: "relative", flexShrink: 0, marginTop: isMobile ? 64 : 78, marginBottom: 28 }}>
        <h3 style={{ fontFamily: F.h, fontSize: isMobile ? 22 : 28, fontWeight: 500, lineHeight: 1.1, letterSpacing: "-.015em", color: "#fff", margin: "0 0 14px" }}>{b.title}</h3>
        <p style={{ fontFamily: F.b, fontSize: 14.5, lineHeight: 1.6, color: "rgba(255,255,255,.66)", margin: 0 }}>{b.desc}</p>
      </div>
    </div>
  );
}

/* ───────── DESKTOP — scroll orizzontale Rail ───────── */
function WhyRailDesktop({ isTablet, onNavigate }) {
  const hz = useHzScroll();
  return (
    <HzStage
      {...hz}
      stickyBg={WHY_RAIL_BG}
      trackBg={HZ_DARK_TRACKBG}
      grid="radial-gradient(rgba(255,255,255,.05) 1px, transparent 1.4px)"
      overlay={<React.Fragment>
        <AuroraDark intensity={.45} />
        <GridOverlay color="rgba(255,255,255,.045)" size={70} opacity={.5} />
      </React.Fragment>}
    >
      <HzDarkRisk isTablet={isTablet} />

      {/* spazio di respiro tra rischio e risposta */}
      <div aria-hidden style={{ flex: "0 0 auto", width: "clamp(90px, 11vw, 220px)", height: "100%" }}/>

      <div style={{ flex: "0 0 auto", height: "100%", display: "flex", alignItems: "center", padding: "0 clamp(28px, 5vw, 90px) 0 0" }}>
        <div style={{ flex: "0 0 auto", width: "clamp(360px, 34vw, 460px)", alignSelf: "center" }}>
          <WhyObscuraIntro onNavigate={onNavigate} />
        </div>
        {HZ_BENEFITS.map(b => <RailColumn key={b.n} b={b} />)}
      </div>
    </HzStage>
  );
}

/* ───────── MOBILE — verticale, stesso linguaggio del rail desktop ───────── */
function WhyRailMobile({ onNavigate }) {
  const accent = "#f87171";
  return (
    <section id="perche" style={{ position: "relative", overflow: "hidden", padding: "70px 0 64px", background: WHY_RAIL_BG, color: "#fff" }}>
      <AuroraDark intensity={.45} />
      <GridOverlay color="rgba(255,255,255,.045)" size={70} opacity={.5} />

      {/* Parte 1 — rischio */}
      <div style={{ position: "relative", zIndex: 1, maxWidth: 1240, margin: "0 auto", padding: "0 24px" }}>
        <h2 style={{ fontFamily: F.h, fontSize: 36, fontWeight: 500, lineHeight: 1.02, letterSpacing: "-.03em", color: "#fff", margin: "0 0 14px" }}>
          Cosa rischi se usi dati sensibili <em style={{ fontStyle: "italic", color: accent, fontWeight: 500 }}>senza anonimizzarli?</em>
        </h2>
        <p style={{ fontFamily: F.b, fontSize: 16, lineHeight: 1.6, color: "rgba(255,255,255,.72)", marginBottom: 24 }}>
          GDPR, AI Act e NIS2 prevedono sanzioni fino a <strong style={{ color: "#fff" }}>€35M</strong>. L&apos;anonimizzazione riduce l&apos;esposizione prima che i documenti entrino nei tuoi workflow AI.
        </p>
        <SanctionCard isTablet />
        <div style={{ marginTop: 28 }}>
          <HzScrollHint isMobile />
        </div>
      </div>

      {/* Parte 2 — intro OBSCURA */}
      <div style={{ position: "relative", zIndex: 1, maxWidth: 1240, margin: "48px auto 0", padding: "0 24px" }}>
        <WhyObscuraIntro onNavigate={onNavigate} isMobile />
      </div>

      {/* Rail colonne — swipe orizzontale, stesso componente desktop */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          marginTop: 36,
          display: "flex",
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          padding: "0 24px 8px",
          WebkitOverflowScrolling: "touch",
          background: HZ_DARK_TRACKBG,
        }}
      >
        {HZ_BENEFITS.map((b, i) => (
          <RailColumn key={b.n} b={b} isMobile isFirst={i === 0} />
        ))}
      </div>
    </section>
  );
}

/* ───────── EXPORT ─────────
   isMobile / isTablet: passali dal tuo hook di breakpoint
   onNavigate: handler del CTA "Anonimizza un documento" */
export default function WhyRail({ isMobile = false, isTablet = false, onNavigate = () => {} }) {
  return (
    <React.Fragment>
      <ObscuraStyle />
      {isMobile
        ? <WhyRailMobile onNavigate={onNavigate} />
        : <WhyRailDesktop isTablet={isTablet} onNavigate={onNavigate} />}
    </React.Fragment>
  );
}
